import { useCallback, useEffect, useState } from 'react';
import { shopApi } from '../../api/shop';
import { useAuth } from '../../auth/AuthProvider';
import { RequireShop } from '../../components/ShopSelector';
import {
  EmptyState,
  ErrorBanner,
  LoadingState,
  PageHeader,
} from '../../components/Layout';
import { useShop } from '../../context/ShopContext';

const DOMAIN_ROLES = new Set(['admin', 'manager']);

function CopyButton({ value, label = 'Copy' }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore clipboard errors
    }
  };

  return (
    <button type="button" className="btn btn-secondary btn-sm" onClick={handleCopy}>
      {copied ? 'Copied' : label}
    </button>
  );
}

export default function SettingsPage() {
  const { getToken } = useAuth();
  const { selectedShopId, selectedShop, reloadShops } = useShop();
  const canManage = DOMAIN_ROLES.has(selectedShop?.role);

  const [shopProfile, setShopProfile] = useState(null);
  const [domainConfig, setDomainConfig] = useState(null);
  const [shopName, setShopName] = useState('');
  const [customDomain, setCustomDomain] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const loadSettings = useCallback(async () => {
    if (!selectedShopId || !canManage) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const [profile, domain] = await Promise.all([
        shopApi.getShop(token, selectedShopId),
        shopApi.getDomainSettings(token, selectedShopId),
      ]);
      setShopProfile(profile);
      setDomainConfig(domain);
      setShopName(profile.name);
      setCustomDomain(domain.custom_domain ?? '');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [canManage, getToken, selectedShopId]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleSaveProfile = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const token = await getToken();
      const updated = await shopApi.updateShop(token, selectedShopId, { name: shopName.trim() });
      setShopProfile(updated);
      await reloadShops();
      setSuccess('Store profile updated.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDomain = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const token = await getToken();
      const data = await shopApi.updateDomainSettings(token, selectedShopId, {
        custom_domain: customDomain.trim() || null,
      });
      setDomainConfig(data);
      setCustomDomain(data.custom_domain ?? '');
      setSuccess('Domain settings saved.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Remove the connected custom domain?')) {
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const token = await getToken();
      const data = await shopApi.updateDomainSettings(token, selectedShopId, {
        custom_domain: null,
      });
      setDomainConfig(data);
      setCustomDomain('');
      setSuccess('Custom domain removed.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const storefrontUrl = domainConfig?.storefront_url || shopProfile?.storefront_url;

  return (
    <RequireShop>
      <PageHeader
        title="Settings"
        description="Manage store profile, storefront URL, and custom domain."
      />

      <ErrorBanner message={error} />
      {success && <div className="success-banner">{success}</div>}

      {!canManage ? (
        <EmptyState message="Only administrators and managers can change settings." />
      ) : loading ? (
        <LoadingState />
      ) : (
        <>
          <div className="card" style={{ marginBottom: '1rem' }}>
            <div className="card-body">
              <h3 className="settings-section-title">Store profile</h3>
              <form className="form-grid" onSubmit={handleSaveProfile}>
                <div className="form-field">
                  <label htmlFor="shop-name">Store name</label>
                  <input
                    id="shop-name"
                    value={shopName}
                    onChange={(event) => setShopName(event.target.value)}
                    disabled={saving}
                    required
                  />
                </div>
                <div className="page-actions">
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving ? 'Saving…' : 'Save profile'}
                  </button>
                </div>
              </form>
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              {domainConfig && (
                <div className="dns-record-card" style={{ marginBottom: '1.5rem' }}>
                  <h3 className="settings-section-title">Your storefront</h3>
                  <p className="settings-section-desc">
                    Your store is available at this address by default.
                  </p>
                  <p className="dns-record-label">Storefront URL</p>
                  <div className="page-actions" style={{ alignItems: 'center' }}>
                    <code className="cname-target">{storefrontUrl}</code>
                    <CopyButton value={storefrontUrl} />
                    <a
                      className="btn btn-secondary btn-sm"
                      href={storefrontUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Open
                    </a>
                  </div>
                </div>
              )}

              <h3 className="settings-section-title">Custom domain</h3>
              <p className="settings-section-desc">
                Connect your own domain with a CNAME record pointing to{' '}
                <code>{domainConfig?.cname_target ?? 'connect.salesy.store'}</code>.
              </p>

              {domainConfig?.custom_domain && domainConfig.domain_status && (
                <p className="dns-record-hint" style={{ marginBottom: '1rem' }}>
                  Status:{' '}
                  <code>
                    {domainConfig.domain_status === 'active'
                      ? 'Active'
                      : domainConfig.domain_status === 'failed'
                        ? 'Failed — check DNS and try again'
                        : 'Pending — waiting for DNS'}
                  </code>
                </p>
              )}

              {domainConfig && (
                <div className="dns-record-card">
                  <p className="dns-record-label">Add this CNAME record at your DNS provider</p>
                  <table className="dns-record-table">
                    <thead>
                      <tr>
                        <th>Type</th>
                        <th>Name / Host</th>
                        <th>Value / Target</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>CNAME</td>
                        <td>
                          <code>{customDomain.trim() || 'www or @'}</code>
                        </td>
                        <td>
                          <code className="cname-target">{domainConfig.cname_target}</code>
                        </td>
                        <td>
                          <CopyButton value={domainConfig.cname_target} />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <p className="dns-record-hint">
                    After you update your DNS with our CNAME record, it may take 5–10 minutes to
                    resolve, and sometimes up to an hour. If your domain is still not connected,
                    contact us at{' '}
                    <a href="mailto:support@salesbotics.io">support@salesbotics.io</a>.
                  </p>
                </div>
              )}

              <form className="form-grid" onSubmit={handleSaveDomain} style={{ marginTop: '1.5rem' }}>
                <div className="form-field">
                  <label htmlFor="custom-domain">Your domain</label>
                  <input
                    id="custom-domain"
                    type="text"
                    placeholder="shop.example.com"
                    value={customDomain}
                    onChange={(event) => setCustomDomain(event.target.value)}
                    disabled={saving}
                  />
                </div>
                <div className="page-actions">
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving ? 'Saving…' : 'Save domain'}
                  </button>
                  {domainConfig?.custom_domain && (
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={handleDisconnect}
                      disabled={saving}
                    >
                      Disconnect
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </>
      )}
    </RequireShop>
  );
}
